import { useRef, useEffect } from 'react';
import { getAchColor, getGrowthColor } from '../../utils/calculations';
import './DataTable.css';

/**
 * DataTable
 *
 * Props:
 *   columns      – column definitions array
 *   data         – row array
 *   loading      – boolean
 *   highlightRow – value of first-column key to highlight
 *
 * Pagination props (all optional — omit for non-paginated tables):
 *   totalRows    – total row count from server
 *   page         – current 1-based page number
 *   pageSize     – rows per page
 *   onPageChange – (newPage: number) => void
 */
const DataTable = ({
  columns,
  data,
  loading,
  highlightRow,
  // pagination
  totalRows,
  page,
  pageSize,
  onPageChange,
}) => {
  const highlightRef = useRef(null);
  const isPaginated  = totalRows != null && onPageChange != null;
  const totalPages   = isPaginated ? Math.max(1, Math.ceil(totalRows / pageSize)) : 1;

  // Scroll highlighted row into view when it changes
  useEffect(() => {
    if (highlightRef.current) {
      highlightRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [highlightRow]);

  if (loading) {
    return (
      <div className="table-skeleton">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="table-skeleton__row" />
        ))}
      </div>
    );
  }

  if (!data?.length) {
    return <div className="table-empty">No data available</div>;
  }

  return (
    <div className="data-table-container">
      <div className="data-table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              {columns.map(col => (
                <th key={col.key} className={`data-table__th data-table__th--${col.align || 'left'}`}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => {
              const isHighlighted = highlightRow && row[columns[0].key] === highlightRow;
              // Check if any text field in the row contains "(VACANT)"
              const isVacant = Object.values(row).some(
                v => typeof v === 'string' && v.toUpperCase().includes('(VACANT)')
              );
              return (
                <tr
                  key={i}
                  ref={isHighlighted ? highlightRef : null}
                  className={`data-table__row${isHighlighted ? ' data-table__row--highlight' : ''}${isVacant ? ' data-table__row--vacant' : ''}`}
                >
                  {columns.map(col => {
                    const val = row[col.key];
                    let cellClass = `data-table__td data-table__td--${col.align || 'left'}`;
                    if (col.key === 'ach_pct')  cellClass += ` ${getAchColor(val)}`;
                    if (col.key === 'grth_pct') cellClass += ` ${getGrowthColor(val)}`;
                    return (
                      <td key={col.key} className={cellClass}>
                        {col.render ? col.render(val, row) : (val != null ? val : '—')}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Pagination bar — only rendered when pagination props are provided ── */}
      {isPaginated && (
        <div className="data-table-pagination">
          <span className="data-table-pagination__info">
            {totalRows === 0
              ? 'No results'
              : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, totalRows)} of ${totalRows}`}
          </span>

          <div className="data-table-pagination__controls">
            {/* First */}
            <button
              className="data-table-pagination__btn"
              onClick={() => onPageChange(1)}
              disabled={page <= 1}
              title="First page"
            >
              «
            </button>

            {/* Prev */}
            <button
              className="data-table-pagination__btn"
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
              title="Previous page"
            >
              ‹
            </button>

            {/* Page numbers — show up to 5 around current */}
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                acc.push(p);
                return acc;
              }, [])
              .map((p, idx) =>
                p === '…'
                  ? <span key={`ellipsis-${idx}`} className="data-table-pagination__ellipsis">…</span>
                  : (
                    <button
                      key={p}
                      className={`data-table-pagination__btn${p === page ? ' data-table-pagination__btn--active' : ''}`}
                      onClick={() => onPageChange(p)}
                    >
                      {p}
                    </button>
                  )
              )}

            {/* Next */}
            <button
              className="data-table-pagination__btn"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages}
              title="Next page"
            >
              ›
            </button>

            {/* Last */}
            <button
              className="data-table-pagination__btn"
              onClick={() => onPageChange(totalPages)}
              disabled={page >= totalPages}
              title="Last page"
            >
              »
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
